import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/cn";
import styles from "./VisitPreviewCard.module.css";
import type {
	PlannerJob,
	ScheduleConflict,
	ScheduledVisit,
	SchedulingService,
} from "./types";

const STATUS_LABELS: Record<ScheduledVisit["status"], string> = {
	planned: "Planned",
	tentative: "Tentative",
	cancelled: "Cancelled",
};

const ACCESS_LABELS: Record<ScheduledVisit["accessStatus"], string> = {
	unknown: "Unknown",
	confirmed: "Confirmed",
	issue: "Issue",
};

function personLabel(assignment: ScheduledVisit["assignments"][number]): string {
	const name = assignment.personName || "Staff member";
	if (assignment.identityStatus === "inactive") return `${name} (inactive)`;
	if (assignment.identityStatus === "missing") return `${name} (no account)`;
	return name;
}

export interface VisitPreviewCardProps {
	id: string;
	visit: ScheduledVisit;
	job: PlannerJob | undefined;
	service: SchedulingService | undefined;
	/** Already narrowed to this visit by the caller. */
	conflicts: ScheduleConflict[];
	/** Viewport rect of the chip this card belongs to. */
	anchor: DOMRect;
	/** True when a tap opened it, so it must say how to close it. */
	touch: boolean;
	onDismiss: () => void;
}

/**
 * The visit behind a calendar chip, in full. Hover, focus or — on touch — a tap
 * opens it; it is deliberately inert (no controls, no links) so that dismissing
 * it can be as cheap as moving the pointer away, and so the chip underneath
 * stays the only way to act on the visit.
 */
export function VisitPreviewCard({
	id,
	visit,
	job,
	service,
	conflicts,
	anchor,
	touch,
	onDismiss,
}: VisitPreviewCardProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(
		null,
	);

	// Placed against the measured card, not an assumed size: the height varies
	// with the crew and the warnings, and a card that runs off the fold is worse
	// than no card at all.
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		const card = element.getBoundingClientRect();
		const margin = 8;
		const gap = 6;
		const left = Math.max(
			margin,
			Math.min(anchor.left, window.innerWidth - margin - card.width),
		);
		const below = anchor.bottom + gap;
		const above = anchor.top - gap - card.height;
		const top =
			below + card.height <= window.innerHeight - margin
				? below
				: above >= margin
					? above
					: Math.max(margin, window.innerHeight - margin - card.height);
		setPosition({ top, left });
	}, [anchor]);

	// Any change of what the card points at invalidates its position, so it
	// closes rather than floating away from its chip.
	useEffect(() => {
		const dismiss = (): void => onDismiss();
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") onDismiss();
		};
		const onPointerDown = (event: PointerEvent): void => {
			const target = event.target as HTMLElement | null;
			// The chip owns its own toggle; anywhere else closes.
			if (target?.closest("[data-visit-chip]") || ref.current?.contains(target)) {
				return;
			}
			onDismiss();
		};
		window.addEventListener("scroll", dismiss, true);
		window.addEventListener("resize", dismiss);
		document.addEventListener("keydown", onKeyDown);
		document.addEventListener("pointerdown", onPointerDown, true);
		return () => {
			window.removeEventListener("scroll", dismiss, true);
			window.removeEventListener("resize", dismiss);
			document.removeEventListener("keydown", onKeyDown);
			document.removeEventListener("pointerdown", onPointerDown, true);
		};
	}, [onDismiss]);

	const trade = service?.label ?? "Not specified";
	const access = ACCESS_LABELS[visit.accessStatus];

	return (
		<div
			ref={ref}
			id={id}
			role="tooltip"
			className={cn(styles.card, !position && styles.measuring)}
			style={position ?? { top: 0, left: 0 }}
		>
			<div className={styles.head}>
				<span className={styles.time}>
					{visit.startTime}–{visit.endTime}
				</span>
				<span
					className={
						visit.status === "cancelled"
							? styles.statusCancelled
							: visit.status === "tentative"
								? styles.statusTentative
								: styles.status
					}
				>
					{STATUS_LABELS[visit.status]}
				</span>
			</div>
			<div>
				<p className={styles.title}>
					{job?.propertyAddress || job?.title || "Unknown job"}
				</p>
				{job?.clientName && <p className={styles.client}>{job.clientName}</p>}
			</div>
			<dl className={styles.facts}>
				<dt>Work</dt>
				<dd>{visit.phase || "Site visit"}</dd>
				<dt>Trade</dt>
				<dd>{trade}</dd>
				<dt>Access</dt>
				<dd>
					{access}
					{visit.accessNote ? ` — ${visit.accessNote}` : ""}
				</dd>
			</dl>
			{visit.accessWindowWarning && (
				<p className={styles.warning}>
					{visit.accessWindowWarning}
					{visit.accessWindowAcknowledged ? " — acknowledged" : ""}
				</p>
			)}
			{visit.assignments.length > 0 ? (
				<ul className={styles.people}>
					{visit.assignments.map((assignment) => (
						<li key={assignment.personId}>
							<span>{personLabel(assignment)}</span>
							<span className={styles.personTime}>
								{assignment.startTime}–{assignment.endTime}
								{assignment.travelBeforeMinutes > 0
									? ` +${assignment.travelBeforeMinutes}m`
									: ""}
							</span>
						</li>
					))}
				</ul>
			) : (
				visit.status !== "cancelled" && (
					<p className={styles.unassignedNote}>Nobody assigned yet</p>
				)
			)}
			{conflicts.map((conflict) => (
				<p className={styles.warning} key={`${conflict.personId}-${conflict.kind}`}>
					{conflict.personName}:{" "}
					{conflict.kind === "travel" ? "travel reserve" : "assignment"}{" "}
					{conflict.startTime}–{conflict.endTime} clashes with leave{" "}
					{conflict.conflictStartTime}–{conflict.conflictEndTime}
				</p>
			))}
			{touch && <p className={styles.dismissHint}>Tap anywhere to close</p>}
		</div>
	);
}
